import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth';
import { authHeaders } from '../../shared/authHeaders';

interface FollowButtonProps {
  profileId: string;
  initialFollowing?: boolean;
  className?: string;
  followClassName?: string;
  followingClassName?: string;
  loadingClassName?: string;
  followingLabel?: string;
  followLabel?: string;
  loadingLabel?: string;
  onFollowChange?: (following: boolean) => void;
}

export function FollowButton({
  profileId,
  initialFollowing = false,
  className,
  followClassName,
  followingClassName,
  loadingClassName,
  followingLabel = 'Following',
  followLabel = 'Follow',
  loadingLabel = '...',
  onFollowChange,
}: FollowButtonProps) {
  const location = useLocation();
  const { getToken, isSignedIn } = useAuth();
  const [isFollowing, setIsFollowing] = useState(initialFollowing);
  const [isSelf, setIsSelf] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(initialFollowing);

  useEffect(() => {
    let cancelled = false;

    async function fetchFollowStatus() {
      setIsSelf(false);
      if (!isSignedIn) {
        setIsFollowing(false);
        setHasCheckedStatus(true);
        return;
      }

      setHasCheckedStatus(false);
      try {
        const token = await getToken();
        const response = await fetch(`/api/follows/${profileId}/following`, {
          headers: authHeaders(token),
        });

        if (response.ok && !cancelled) {
          const data = await response.json();
          setIsFollowing(Boolean(data.isFollowing));
          setIsSelf(Boolean(data.isSelf));
        }
      } catch (error) {
        console.error('Error checking follow status:', error);
      } finally {
        if (!cancelled) {
          setHasCheckedStatus(true);
        }
      }
    }

    fetchFollowStatus();
    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn, profileId]);

  const isCheckingStatus = isSignedIn && !hasCheckedStatus;
  const buttonClassName = isCheckingStatus
    ? loadingClassName ?? className ?? 'btn-follow'
    : isFollowing
      ? followingClassName ?? 'btn-follow'
      : followClassName ?? className ?? 'btn-follow';

  if (isSelf) {
    return null;
  }

  if (!isSignedIn) {
    const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    return (
      <Link to={`/sign-in?redirect_url=${redirectUrl}`} className={buttonClassName}>
        {followLabel}
      </Link>
    );
  }

  async function handleFollow(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();

    if (isCheckingStatus) return;

    setLoading(true);
    try {
      const token = await getToken();
      const method = isFollowing ? 'DELETE' : 'POST';
      const response = await fetch(`/api/follows/${profileId}`, {
        method,
        headers: authHeaders(token),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update follow');
      }

      const nextFollowing = !isFollowing;
      setIsFollowing(nextFollowing);
      onFollowChange?.(nextFollowing);
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleFollow}
      disabled={loading || Boolean(isCheckingStatus)}
      className={buttonClassName}
      aria-pressed={isFollowing}
    >
      {loading || isCheckingStatus ? loadingLabel : isFollowing ? followingLabel : followLabel}
    </button>
  );
}
